import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { CheckCircle, ChatCircleDots, CreditCard, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";

// package_id maps to server-side PACKAGES. When null, this line item is quote-only.
const LINE_ITEMS = [
  {
    group: "Federal returns",
    items: [
      { name: "Federal — Standard Return (Form 1040 & 1040-NR)", price: 29.99, package_id: "individual", note: "For most W-2 and 1099 filers." },
      { name: "Federal — ITIN (Paper Filing) — Form 1040", price: 199.99, package_id: "itin", note: "Includes W-7 and CAA-guided authentication." },
      { name: "Federal — Non-Resident Spouse Election (6013 G&H)", price: 99.99, package_id: null, note: "Prepared alongside your 1040." },
      { name: "Federal — Amended Return (1040-X)", price: 149.99, package_id: null, note: "Corrections to a previously filed return." },
    ],
  },
  {
    group: "State, city & county",
    items: [
      { name: "Each State Return", price: 19.99, package_id: null, note: "$19.99 per additional state added to your federal." },
      { name: "State OSTC & Other Credits", price: 14.99, package_id: null, note: "Out-of-state tax credit and misc. state credits." },
      { name: "Each City / County Return", price: 14.99, package_id: null, note: "Local jurisdiction filings (OH, PA, KY, NY, etc.)." },
    ],
  },
  {
    group: "Schedules & special situations",
    items: [
      { name: "Each Schedule (A, C, D, E, SE, etc.)", price: 49.99, package_id: null, note: "Itemized deductions, self-employment, rental, capital gains." },
      { name: "Stock Transactions (per Page)", price: 9.99, package_id: null, note: "Brokerage summary; per page of 1099-B activity." },
      { name: "FBAR (FinCEN 114)", price: 149.0, package_id: "fbar", note: "50% off when bundled with a paid return." },
    ],
  },
  {
    group: "Business",
    items: [
      { name: "Business Tax Filing (1120, 1120-S, 1065)", price: 899.0, package_id: "business", note: "Includes federal + K-1s + depreciation." },
      { name: "Pro (Multi-state + Self-employed)", price: 499.0, package_id: "pro", note: "Everything in Individual + Schedule C + multi-state + 1 notice." },
      { name: "Federal / State Extension", price: 49.0, package_id: "extension", note: "Form 4868 or 7004 filed same day." },
    ],
  },
  {
    group: "Representation & planning",
    items: [
      { name: "Comprehensive Tax Representation (up to 8 years)", price: null, package_id: null, badge: "FREE consult", note: "Complimentary consult for prior-year cleanups." },
      { name: "Strategically Optimized Tax Planning", price: null, package_id: null, badge: "Custom quote", note: "Multi-year planning, entity structure, QBI optimization." },
      { name: "IRS Notice Assistance (per notice)", price: 249.0, package_id: null, note: "CP2000, CP14, LT11, audit correspondence." },
    ],
  },
];

function priceLabel(item) {
  if (item.badge) return item.badge;
  if (item.price === null) return "Custom";
  return `$${item.price.toFixed(2)}`;
}

export default function Pricing() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  useSeo({
    title: "Individual & Compliance Pricing | Taxfree Filer LLP",
    description: "Transparent, itemized US tax filing prices — federal, state, schedules, FBAR, business and representation. Pay only when your return is ready to file.",
    robots: "index,follow",
    canonical: "https://taxfreefiler.com/pricing",
  });

  const checkout = async (pkg, label) => {
    setBusy(pkg);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: pkg, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b tf-hairline">
        <div className="absolute inset-0 tf-grid opacity-60" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-20">
          <div className="max-w-3xl">
            <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">Pricing</div>
            <h1 className="mt-3 font-[Chivo] text-5xl lg:text-6xl font-black text-[#0A2540] tracking-tight" data-testid="pricing-title">
              Individual & Compliance Pricing
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Start your preparation for free. Pay only when you file. No hidden fees, no hourly billing surprises — every service is priced up-front.
            </p>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-700">
              <span className="inline-flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-emerald-600" /> Flat per-item pricing</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-emerald-600" /> Pay on approval</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-emerald-600" /> IRS e-File authorized</span>
            </div>
          </div>
        </div>
      </section>

      {/* LINE ITEMS */}
      <section className="mx-auto max-w-6xl px-6 lg:px-8 py-12 lg:py-16 space-y-14">
        {LINE_ITEMS.map((group) => (
          <div key={group.group}>
            <div className="flex items-baseline justify-between">
              <h2 className="font-[Chivo] text-2xl lg:text-3xl font-black text-[#0A2540] tracking-tight">{group.group}</h2>
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Flat fee</div>
            </div>
            <div className="mt-6 rounded-lg border tf-hairline bg-white overflow-hidden" data-testid={`pricing-group-${group.group.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
              {group.items.map((item, idx) => (
                <div key={item.name} className={`grid grid-cols-12 gap-4 px-5 lg:px-8 py-5 items-center ${idx > 0 ? "border-t tf-hairline" : ""}`}>
                  <div className="col-span-12 md:col-span-7">
                    <div className="font-[Chivo] font-bold text-[#0A2540]">{item.name}</div>
                    {item.note && <div className="mt-1 text-xs text-slate-500">{item.note}</div>}
                  </div>
                  <div className="col-span-6 md:col-span-2 md:text-right">
                    <div className="font-[Chivo] text-xl font-black text-[#0A2540]">{priceLabel(item)}</div>
                  </div>
                  <div className="col-span-6 md:col-span-3 flex md:justify-end gap-2">
                    {item.package_id ? (
                      <Button
                        size="sm"
                        onClick={() => checkout(item.package_id, item.name)}
                        disabled={busy === item.package_id}
                        className="bg-[#D32F2F] hover:bg-[#D32F2F]/90"
                        data-testid={`pricing-pay-${item.package_id}`}
                      >
                        <CreditCard size={14} className="mr-1" /> {busy === item.package_id ? "Loading…" : "Pay now"}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate("/contact")}
                        className="border-[#0A2540] text-[#0A2540]"
                        data-testid={`pricing-quote-${item.name.slice(0,10).replace(/[^a-z]+/gi, "-").toLowerCase()}`}
                      >
                        <ChatCircleDots size={14} className="mr-1" /> Get quote
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* CTA */}
      <section className="bg-[#0A2540] text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs font-mono uppercase tracking-widest">
              <Sparkle size={12} weight="fill" className="text-[#D32F2F]" /> Free to start
            </div>
            <h2 className="mt-5 font-[Chivo] text-3xl lg:text-5xl font-black tracking-tight">Start your preparation for free. Pay only when you file.</h2>
            <p className="mt-4 text-slate-300 max-w-lg">Open a client account, upload your W-2s and 1099s, and review everything with a licensed preparer before you approve.</p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Link to="/auth?tab=register"><Button size="lg" className="bg-[#D32F2F] hover:bg-[#D32F2F]/90 h-12 px-6" data-testid="pricing-cta-register">Open a Client Account</Button></Link>
            <Link to="/contact"><Button size="lg" variant="outline" className="h-12 px-6 border-white/30 text-white hover:bg-white hover:text-[#0A2540]" data-testid="pricing-cta-contact">Talk to an advisor</Button></Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
